import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Layers, Loader2, Search } from 'lucide-react';
import { getPublicPromoterCollectionsByPhone } from '../api/promotionRequest.api';
import FabricCatalogueView from './FabricCatalogueView';

const PublicPromoterCollectionsView = ({ phoneNumber, onBack, showNotification }) => {
  const normalizedPhone = phoneNumber ? String(phoneNumber).replace(/\D/g, '') : '';

  const [promoter, setPromoter] = useState(null);
  const [collections, setCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const observerTarget = useRef(null);
  const searchTimeoutRef = useRef(null);

  const fetchCollections = useCallback(
    async (page = 1, append = false) => {
      if (!normalizedPhone) return;
      if (append) setIsLoadingMore(true);
      else {
        setIsLoading(true);
        setError(null);
      }

      try {
        const response = await getPublicPromoterCollectionsByPhone(normalizedPhone, page, 20, searchQuery);
        const { promoter: promoterInfo, collections: fetchedCollections, pagination } = response || {};

        setPromoter(promoterInfo || null);
        if (append) setCollections((prev) => [...prev, ...(fetchedCollections || [])]);
        else setCollections(fetchedCollections || []);

        setCurrentPage(pagination?.currentPage || page);
        setHasNextPage(Boolean(pagination?.hasNextPage));
      } catch (e) {
        console.error('Error fetching promoter collections:', e);
        const msg = e.response?.data?.message || 'Failed to load promoted catalogue';
        setError(msg);
        showNotification?.(msg, 'error');
        if (!append) setCollections([]);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [normalizedPhone, searchQuery, showNotification]
  );

  useEffect(() => {
    if (!normalizedPhone) return;
    fetchCollections(1, false);
  }, [normalizedPhone, fetchCollections]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    if (!normalizedPhone) return;
    fetchCollections(1, false);
  }, [searchQuery, normalizedPhone, fetchCollections]);

  useEffect(() => {
    if (!hasNextPage || isLoading || isLoadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasNextPage && !isLoading && !isLoadingMore) {
          fetchCollections(currentPage + 1, true);
        }
      },
      { threshold: 0.1, rootMargin: '120px' }
    );

    const cur = observerTarget.current;
    if (cur) observer.observe(cur);
    return () => {
      if (cur) observer.unobserve(cur);
      observer.disconnect();
    };
  }, [hasNextPage, isLoading, isLoadingMore, currentPage, fetchCollections]);

  const getFabricImage = (fabric) => {
    if (!fabric) return null;
    return (
      fabric.image ||
      fabric.images?.original?.s3Url ||
      fabric.images?.original?.imgbbUrl ||
      fabric.images?.original?.url ||
      fabric.images?.preview?.s3Url ||
      fabric.images?.preview?.url ||
      null
    );
  };

  const allFabrics = collections.flatMap((c) => c?.fabrics || []).filter(Boolean);

  if (selectedFabric) {
    return (
      <FabricCatalogueView
        fabric={selectedFabric}
        onClose={() => setSelectedFabric(null)}
        showNotification={showNotification}
        allowShadeCardUpload={false}
      />
    );
  }

  const title = promoter?.brandName || promoter?.name || 'Promoter';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-white/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 flex items-center gap-3 sm:gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-full bg-white/60 hover:bg-white transition-colors shadow-sm"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-semibold text-gray-900 truncate">{title}</h1>
            <p className="text-xs sm:text-sm text-gray-500">
              {collections.length} {collections.length === 1 ? 'collection' : 'collections'} · {allFabrics.length}{' '}
              {allFabrics.length === 1 ? 'fabric' : 'fabrics'}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search promoted collections…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white/90 border border-white rounded-2xl shadow-inner text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pb-6">
        {isLoading && collections.length === 0 ? (
          <div className="flex items-center justify-center py-12 sm:py-16">
            <div className="text-center">
              <Loader2 size={48} className="text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 text-sm sm:text-base">Loading catalogue…</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 sm:p-12 text-center">
            <p className="text-red-600 text-sm sm:text-base">{error}</p>
            <button
              onClick={() => fetchCollections(1, false)}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base"
            >
              Try Again
            </button>
          </div>
        ) : collections.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 sm:p-12 text-center">
            <Layers size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No promoted collections found</h3>
            <p className="text-sm sm:text-base text-gray-500">
              {searchQuery ? 'Try a different search term.' : 'Nothing is public for this promoter yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {collections.map((collection) => {
                const collectionImage = collection?.image?.url || null;
                const fabrics = collection?.fabrics || [];
                return (
                  <div
                    key={collection._id}
                    className="group bg-white/90 backdrop-blur border border-white/80 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                  >
                    <div className="relative aspect-[4/5] bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 overflow-hidden">
                      {collectionImage ? (
                        <img
                          src={collectionImage}
                          alt={collection.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full px-4">
                          <span className="text-base font-semibold text-purple-600 text-center break-words">
                            {collection?.name || 'Collection'}
                          </span>
                        </div>
                      )}
                      <div className="absolute top-2 left-2 text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-purple-600 text-white shadow">
                        Collection
                      </div>
                      <div className="absolute top-2 right-2 text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-blue-500 text-white shadow">
                        {fabrics.length} {fabrics.length === 1 ? 'fabric' : 'fabrics'}
                      </div>
                    </div>
                    <div className="p-3 sm:p-4 space-y-2">
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">{collection?.name}</h3>
                      {fabrics?.length ? (
                        <div className="grid grid-cols-3 gap-2">
                          {fabrics.slice(0, 3).map((fabric) => {
                            const img = getFabricImage(fabric);
                            return (
                              <button
                                key={fabric?._id || fabric?.name}
                                type="button"
                                onClick={() => setSelectedFabric(fabric)}
                                className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:border-blue-300"
                                title={fabric?.name || 'Fabric'}
                              >
                                {img ? (
                                  <img src={img} alt={fabric?.name || ''} className="w-full h-full object-cover" loading="lazy" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-500 px-1">
                                    {String(fabric?.name || 'Fabric').slice(0, 12)}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">No public fabrics in this collection.</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasNextPage && (
              <div ref={observerTarget} className="py-6 flex justify-center">
                {isLoadingMore ? <Loader2 size={32} className="text-blue-500 animate-spin" /> : null}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicPromoterCollectionsView;

